import { useQuery } from "@tanstack/react-query";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import type {
  MeteringOrMarketLocation,
  Person,
  Storage
} from "../../../../utils/backend-types";
import { ObjectName } from "../../../../utils/enums";
import { RoundedNumberFormat } from "../../../../utils/RoundedNumberFormat";
import { meteringServiceProviderQueries } from "../../../AdminDashboard/MeteringServiceProviderTable/hooks/useMeteringServiceProvider";
import type { ConfiguratorComponent } from "../ComponentTable";
import { getConnectionCustomColumns } from "./getConnectionCustomColumns";
import { getGasConnectionCustomColumns } from "./getGasConnectionCustomColumns";
import { getMeterCustomColumns } from "./getMeterCustomColumns";
import { getNonStaffLocationColumns } from "./getNonStaffLocationColumns";
import { getStaffLocationColumns } from "./getStaffLocationColumns";

interface GetGasConnectionCustomColumnsProps {
  objectName: ObjectName;
  isStaff: boolean;
  persons?: Array<Person>;
  meteringOrMarketLocations?: Array<MeteringOrMarketLocation>;
  projectId: string;
  siteId: number;
}

function getCustomColumns({
  objectName,
  isStaff,
  persons,
  meteringOrMarketLocations,
  projectId,
  siteId
}: GetGasConnectionCustomColumnsProps): Array<
  MRT_ColumnDef<ConfiguratorComponent>
> {
  switch (objectName) {
    case ObjectName.Consumer:
      return [
        {
          header: "Typ",
          accessorKey: "typeLabel"
        }
      ];

    case ObjectName.GasConnection:
      return getGasConnectionCustomColumns({
        persons,
        projectId,
        siteId
      }) as Array<MRT_ColumnDef<ConfiguratorComponent>>;

    case ObjectName.Storage:
      return [
        {
          header: "Speicherleistung",
          accessorKey: "installedCapacity",
          Cell: ({ cell }) => (
            <InstalledCapacityCell
              value={cell.getValue<Storage["installedCapacity"]>()}
            />
          )
        }
      ];

    case ObjectName.Connection:
      return getConnectionCustomColumns({
        persons,
        projectId,
        siteId
      });

    case ObjectName.Meter:
      return getMeterCustomColumns({
        meteringOrMarketLocations,
        projectId,
        siteId
      });

    case ObjectName.MarketLocation:
    case ObjectName.MeteringLocation:
      return [
        {
          header:
            objectName === ObjectName.MeteringLocation
              ? "Messlokationsnummer"
              : "Marktlokationsnummer",
          accessorKey: "number"
        },
        {
          header: "Messstellenbetreiber",
          accessorKey: "meteringServiceProvider",
          Cell: ({ cell }) => {
            const value =
              cell.getValue<
                MeteringOrMarketLocation["meteringServiceProvider"]
              >();
            if (value) {
              return <MeteringServiceProviderCell id={value} />;
            }
          }
        },
        {
          header: "Messrichtungen",
          accessorFn: (rowData) =>
            (rowData as MeteringOrMarketLocation).measurands.join(", ")
        },
        ...(isStaff
          ? getStaffLocationColumns(objectName)
          : getNonStaffLocationColumns())
      ];

    case ObjectName.Person:
    case ObjectName.GhostNode:
    case ObjectName.MeteringOrMarketLocation:
    default:
      return [];
  }
}

export function MeteringServiceProviderCell({ id }: { id: number }) {
  const { data } = useQuery(meteringServiceProviderQueries.detail(id));

  if (!id) {
    return null;
  }

  return <span>{(data?.customLabel || data?.name) ?? "Wird geladen..."}</span>;
}

export function InstalledCapacityCell({ value }: { value: number | null }) {
  if (!value) {
    return null;
  }

  return (
    <RoundedNumberFormat
      decimalScale={4}
      decimalSeparator=","
      displayType="text"
      suffix=" kW"
      thousandSeparator="."
      value={value}
    />
  );
}

export { getCustomColumns };
